<template>
  <BaseDialog
    :title="displayData.modal.title"
    :btn1-loading="loading"
    :close-on-click-modal="false"
    @close="$emit('close')"
    @confirm="onConfirm"
  >
    <BaseElForm
      ref="formRef"
      v-loading="!loaded"
      :model="formData"
      :rules="formRules"
      label-position="top"
    >
      <BaseElFormItem>
        <div>
          <p>{{ $t('branchSetting.dialog.imageSetting.title') }}</p>
          <p>{{ $t('branchSetting.dialog.imageSetting.subTitle') }}</p>
        </div>
        <UploadButton
          :img="formData.img"
          :isAvatar="true"
          @change="onImaChange"
        />
      </BaseElFormItem>
      <BaseElFormItem :label="$t('branchSetting.dialog.branchName.title')" prop="name">
        <BaseElInput v-model="formData.name" :placeholder="$t('branchSetting.dialog.branchName.placeholder')" />
      </BaseElFormItem>

      <BaseElFormItem :label="$t('branchSetting.dialog.phone.title')" prop="phone">
        <BaseElInput v-model="formData.phone" :placeholder="$t('branchSetting.dialog.phone.placeholder')" />
      </BaseElFormItem>

      <BaseElFormItem :label="$t('branchSetting.dialog.address.title')" prop="address">
        <BaseElInput v-model="formData.address" :placeholder="$t('branchSetting.dialog.address.placeholder')" />
      </BaseElFormItem>

      <BaseElFormItem :label="$t('branchSetting.dialog.googleMapLink.title')" prop="googleMapLink">
        <BaseElInput v-model="formData.googleMapLink" :placeholder="$t('branchSetting.dialog.googleMapLink.placeholder')" />
      </BaseElFormItem>

      <BaseElFormItem :label="$t('branchSetting.dialog.email.title')" prop="email">
        <BaseElInput v-model="formData.email" :placeholder="$t('branchSetting.dialog.email.placeholder')" />
      </BaseElFormItem>
      <BaseElFormItem :label="$t('branchSetting.dialog.currency.title')" prop="currency">
        <BaseElSelect
          v-model="formData.currency"
          :placeholder="$t('branchSetting.dialog.currency.placeholder')"
          :disabled="disabledControl.formItem.currency"
        >
          <BaseElSelectOption
            v-for="option in currencyOptions"
            :key="option.value"
            :value="option.value"
            :label="option.label"
          />
        </BaseElSelect>
      </BaseElFormItem>
      <BaseElFormItem :label="$t('branchSetting.dialog.timezone.title')" prop="timezone">
        <template #label>
          <div class="inline-flex flex-col" style="gap: 4px">
            <p>{{ $t('branchSetting.dialog.timezone.title') }}</p>
            <p v-show="showControl.formItemHint.timezone" class="text-sub text-gray-50">
              {{ displayData.formItemHint.timezone }}
            </p>
          </div>
        </template>
        <BaseElSelect
          v-model="formData.timezone"
          :placeholder="$t('branchSetting.dialog.timezone.placeholder')"
          :disabled="disabledControl.formItem.timezone"
        >
          <BaseElSelectOption
            v-for="option in timezoneOptions"
            :key="option.value"
            :value="option.value"
            :label="option.label"
          />
        </BaseElSelect>
      </BaseElFormItem>
      <BaseElFormItem :label="$t('branchSetting.dialog.language.title')" prop="language">
        <BaseElSelect
          v-model="formData.language"
          :placeholder="$t('branchSetting.dialog.language.placeholder')"
        >
          <BaseElSelectOption value="zh-TW" label="繁體中文" />
          <BaseElSelectOption value="en-US" label="English" />
          <BaseElSelectOption value="ja-JP" label="日本語" />
          <BaseElSelectOption value="ko-KR" label="한국어" />
          <BaseElSelectOption value="vi-VN" label="Tiếng Việt" />
          <BaseElSelectOption value="th-TH" label="ไทย" />
        </BaseElSelect>
      </BaseElFormItem>

      <!-- GIMMATEK 一間分店代表一位服務人員：儀錶板顯示綁定的預約服務人員，選項為此分店的服務人員；新增分店時由後端建立同名的服務人員並綁定 -->
      <BaseElFormItem prop="DashboardAppointmentUnitId">
        <template #label>
          <div class="inline-flex flex-col" style="gap: 4px">
            <p>{{ $t('branchSetting.dialog.dashboardAppointmentUnit.title') }}</p>
            <p class="text-sub text-gray-50">{{ $t('branchSetting.dialog.dashboardAppointmentUnit.hint') }}</p>
          </div>
        </template>
        <BaseElSelect
          v-model="formData.DashboardAppointmentUnitId"
          clearable
          :disabled="!isEdit"
          :placeholder="isEdit ? $t('branchSetting.dialog.dashboardAppointmentUnit.placeholder') : $t('branchSetting.dialog.dashboardAppointmentUnit.createPlaceholder')"
        >
          <BaseElSelectOption
            v-for="unit in appointmentUnitOptions"
            :key="unit.id"
            :value="unit.id"
            :label="unit.name"
          />
        </BaseElSelect>
      </BaseElFormItem>

      <BaseElFormItem :label="$t('branchSetting.dialog.industryCategory.title')" prop="industry">
        <div class="grid gap-[8px] grid-flow-col">
          <BaseElSelect v-model="formData.industryCategory" :placeholder="$t('branchSetting.dialog.industryCategory.placeholder')" @change="updateIndustry">
            <BaseElSelectOption
              v-for="option in industryCategories"
              :key="option.key"
              :value="option.key"
              :label="option.name"
            />
          </BaseElSelect>
          <BaseElSelect v-model="formData.industry" :disabled="!formData.industryCategory" :placeholder="$t('branchSetting.dialog.subIndustryCategory.placeholder')">
            <BaseElSelectOption
              v-for="option in subIndustryCategories"
              :key="option.key"
              :value="option.key"
              :label="option.name"
            />
          </BaseElSelect>
        </div>
      </BaseElFormItem>
      <BaseElFormItem :label="$t('branchSetting.dialog.contract.title')" prop="ContractPlanId">
        <ContractPlanSelect
          :mode="isEdit ? 'update' : 'create'"
          :shopPlan="formData.ContractPlan"
          :model.sync="formData.ContractPlanId"
        />
      </BaseElFormItem>
    </BaseElForm>

    <div slot="footer">
      <BaseElButton
        plain
        @click=";(showDialog = false), resetForm()"
      >
        {{ $t('common.button.cancel.text') }}
      </BaseElButton>
      <BaseElButton :loading="loading" type="primary" @click="onConfirm">
        {{
          displayData.modal.btn.confirm
        }}
      </BaseElButton>
    </div>

    <ImageCropper
      v-if="modal.cropper"
      :image="imgHolder"
      @close="modal.cropper = false"
      @uploaded="getCroppedImage"
    />
  </BaseDialog>
</template>

<script>
import { computed, defineComponent, inject, onBeforeMount, reactive, ref } from 'vue'
import BaseDialog from '@/components/Dialog/BaseDialog.vue'
import { useBaseForm } from '@/use/useForm'
import { CreateShop, GetShopById, UpdateShop } from '@/api/shop'
import { GetServiceUnit } from '@/api/serviceUnit'
import { find, some, get } from 'lodash'
import store from '@/store'
import notifyMessage from '@/config/notifyMessage'
import ContractPlanSelect from '@/components/Select/ContractPlanSelect.vue'
import UploadButton from '@/components/Button/UploadButton.vue'
import { useOrg } from '@/use/useOrg'
import { currencyOptions, timezoneOptions } from '@/config/branchSetting'
import { i18n } from '@/plugins/i18n/i18n'
import { emailRulesNoRequired, noEmptyRules, phoneRules } from '@/validation'
import ImageCropper from '@/components/ImageCropper.vue'
import { apiFormatAdaptor } from '@/utils/api'

export default defineComponent({
  name: 'EditBranchShopModal',
  components: {
    BaseDialog,
    ContractPlanSelect,
    UploadButton,
    ImageCropper,
  },
  props: {
    selectRow: {
      type: Object,
      default: () => ({}),
    },
  },
  setup (props, { emit }) {
    const industryCategories = inject('industryCategories')
    const { formData, formRef, initFormData, loading, checkForm } = useBaseForm()
    const { dashboardConfig } = useOrg()
    const loaded = ref(false)
    const modal = reactive({
      cropper: false,
    })
    initFormData({
      img: null,
      name: '',
      phone: '',
      address: '',
      email: '',
      industryCategory: '',
      industry: '',
      ContractPlanId: '',
      googleMapLink: '',
      currency: 'TWD',
      timezone: 'Asia/Taipei',
      language: 'zh-TW',
      DashboardAppointmentUnitId: '',
    })

    const subIndustryCategories = ref([])
    const imgHolder = ref(null)
    const appointmentUnitOptions = ref([])

    const isEdit = computed(() => get(props.selectRow, 'id'))
    const showControl = computed(() => {
      const controls = {
        formItem: {
          timezone: true,
          currency: true,
        },
        formItemHint: {
          timezone: true,
        },
      }

      if (isEdit.value) {
        controls.formItemHint.timezone = false
      }

      return controls
    })

    const disabledControl = computed(() => {
      const controls = {
        formItem: {
          currency: false,
          timezone: false,
        },
      }

      if (isEdit.value) {
        controls.formItem.currency = true
        controls.formItem.timezone = true
      }

      if (!get(dashboardConfig.value, 'orgModulePermission.enableShopTimezone')) {
        controls.formItem.timezone = true
      }
      if (!get(dashboardConfig.value, 'orgModulePermission.enableShopCurrency')) {
        controls.formItem.currency = true
      }

      return controls
    })

    const formRules = computed(() => {
      const rules = {
        name: [noEmptyRules()],
        phone: [noEmptyRules(), phoneRules(true, true, true)],
        address: [noEmptyRules()],
        // prefecture: [noEmptyRules()],
        // city: [noEmptyRules()],
        // detailAddress: [noEmptyRules()],
        email: [emailRulesNoRequired()],
        currency: [noEmptyRules()],
        timezone: [noEmptyRules()],
        ContractPlanId: [noEmptyRules()],
        language: [noEmptyRules()],
      }

      return rules
    })

    const displayData = computed(() => {
      const data = {
        modal: {
          title: isEdit.value ? i18n.t('branchSetting.dialog.edit.title') : i18n.t('branchSetting.dialog.create.title'),
          btn: {
            confirm: isEdit.value ? i18n.t('common.button.save.text') : i18n.t('common.button.create.text'),
          },
        },
        formItemHint: {
          timezone: i18n.t('branchSetting.dialog.timezone.hint'),
        },
      }
      return data
    })

    const submitPayload = computed(() => {
      const enableCurrency = get(dashboardConfig.value, 'orgModulePermission.enableShopCurrency')
      const enableTimezone = get(dashboardConfig.value, 'orgModulePermission.enableShopTimezone')
      return {
        shopId: get(props.selectRow, 'id'),
        imageId: formData.img?.id || null,
        name: formData.name,
        phone: formData.phone || undefined,
        address: formData.address || undefined,
        email: formData.email || undefined,
        industry: formData.industry || undefined,
        contractPlanId: formData.ContractPlanId !== 'none' ? formData.ContractPlanId : undefined,
        googleMapLink: formData.googleMapLink || null,
        currency: enableCurrency ? formData.currency : undefined, // null -> undefined CNCY-6391
        timezone: enableTimezone ? formData.timezone : undefined,
        language: formData.language,
        DashboardAppointmentUnitId: isEdit.value ? formData.DashboardAppointmentUnitId || null : undefined,
      }
    })

    // 分店沒有開預約模組時 API 會失敗，選項維持空的
    const getAppointmentUnitOptions = async () => {
      const [res, err] = await apiFormatAdaptor(GetServiceUnit)({ shopId: props.selectRow.id, start: 0, limit: 100 })
      if (err) return
      appointmentUnitOptions.value = res
    }

    const syncFormData = async () => {
      const [[res, err]] = await Promise.all([GetShopById(props.selectRow.id), getAppointmentUnitOptions()])
      if (err) return window.$message.error(err || err.msg)

      formData.img = res.Image || undefined
      formData.name = res.name
      formData.phone = res.phone
      formData.address = res.address
      formData.email = res.email
      formData.industryCategory = res.industryCategory
      formData.industry = res.industry
      formData.ContractPlanId = get(res, 'ContractPlan.id')
      formData.googleMapLink = res.googleMapLink
      formData.currency = res.currency
      formData.timezone = res.timezone || 'Asia/Taipei'
      formData.language = res.language || 'zh-TW'
      // 綁定的服務人員已刪除時不在選項內，視為未綁定，儲存時一併清掉
      formData.DashboardAppointmentUnitId = find(appointmentUnitOptions.value, { id: res.DashboardAppointmentUnitId }) ? res.DashboardAppointmentUnitId : ''

      if (res.industry) {
        const industryCategory = find(industryCategories.value, category =>
          some(category.industries, industry => industry.key === res.industry),
        )
        formData.industryCategory = industryCategory.key
        subIndustryCategories.value = industryCategory.industries
      }

      // if (isOverseasShop.value && config.address) {
      //   const address = config.address.split(',')
      //   formData.prefecture = address[0]
      //   formData.city = address[1]
      //   formData.detailAddress = address[2]
      // }
    }

    const updateIndustry = () => {
      formData.industry = ''
      const sub = find(industryCategories.value,
        (category) => category.key === formData.industryCategory,
      )
      subIndustryCategories.value = sub.industries
    }

    const onImaChange = (img) => {
      imgHolder.value = img
      modal.cropper = true
    }

    const getCroppedImage = (e) => {
      formData.img = e
      modal.cropper = false
    }

    const onConfirm = async () => {
      loading.value = true
      const formPass = await checkForm(formRef.value)
      if (!formPass) {
        loading.value = false
        return
      }

      let apiMethod = apiFormatAdaptor(CreateShop)
      let successMsg = notifyMessage.createSuccess
      if (isEdit.value) {
        apiMethod = apiFormatAdaptor(UpdateShop)
        successMsg = notifyMessage.updateSuccess
      }

      const [shop, errMsg, rawErr] = await apiMethod(submitPayload.value)
      loading.value = false
      if (errMsg) {
        window.$message.error(errMsg)
        return
      }
      window.$message.success(successMsg)
      if (!isEdit.value) {
        await store.dispatch('user/addAllowShop', shop)
      }

      emit('refresh')
      emit('close')
    }

    onBeforeMount(async () => {
      if (isEdit.value) await syncFormData()
      loaded.value = true
    })

    return {
      loaded,
      displayData,
      formRules,
      formData,
      formRef,
      loading,
      onConfirm,
      showControl,
      currencyOptions,
      timezoneOptions,
      disabledControl,
      subIndustryCategories,
      industryCategories,
      updateIndustry,
      isEdit,
      onImaChange,
      modal,
      imgHolder,
      getCroppedImage,
      appointmentUnitOptions,
    }
  },
})
</script>

<style lang="postcss" scoped>

</style>
